/**
 * ErrorDisplay Component
 *
 * A reusable component for displaying error states with consistent styling.
 * Used for API errors, loading failures, and other error scenarios.
 */

import { Button } from '@/components/ui';

// =============================================================================
// Types
// =============================================================================

export interface ErrorDisplayProps {
  /** Error title/heading */
  title: string;
  /** Error message/description */
  message?: string;
  /** Optional error code (e.g., "Error: 502") */
  errorCode?: string | number;
  /** Icon to display (default: "pi-exclamation-triangle") */
  icon?: 'warning' | 'error' | 'info';
  /** Retry button text (default: "Try again") */
  retryText?: string;
  /** Callback when retry button is clicked */
  onRetry?: () => void;
  /** Optional secondary action button */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'secondary' | 'ghost' | 'destructive';
  };
  /** Custom className for the container */
  className?: string;
  /** Whether to show in full-page centered layout (default: true) */
  fullPage?: boolean;
}

// =============================================================================
// Icon Configuration
// =============================================================================

const iconConfig = {
  warning: {
    className: 'pi pi-exclamation-triangle text-4xl text-status-error',
  },
  error: {
    className: 'pi pi-exclamation-circle text-4xl text-error-500',
  },
  info: {
    className: 'pi pi-info-circle text-4xl text-icon-secondary',
  },
} as const;

// =============================================================================
// Component
// =============================================================================

export function ErrorDisplay({
  title,
  message,
  errorCode,
  icon = 'warning',
  retryText = 'Try again',
  onRetry,
  secondaryAction,
  className = '',
  fullPage = true,
}: ErrorDisplayProps) {
  const iconClass = iconConfig[icon].className;

  const content = (
    <div className={`flex flex-col items-center justify-center text-center ${className}`}>
      {/* Icon */}
      <i className={`${iconClass} mb-4`} />

      {/* Title */}
      <h3 className="text-lg font-medium text-text-primary mb-2">{title}</h3>

      {/* Error Code */}
      {errorCode && (
        <p className="text-sm text-text-secondary mb-2">
          {typeof errorCode === 'number' ? `Error: ${errorCode}` : errorCode}
        </p>
      )}

      {/* Message */}
      {message && (
        <p className={`text-sm text-text-secondary max-w-md ${errorCode ? 'mb-4' : 'mb-4'}`}>
          {message}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 mt-2">
        {onRetry && (
          <Button variant="default" onClick={onRetry}>
            {retryText}
          </Button>
        )}
        {secondaryAction && (
          <Button
            variant={secondaryAction.variant || 'secondary'}
            onClick={secondaryAction.onClick}
          >
            {secondaryAction.label}
          </Button>
        )}
      </div>
    </div>
  );

  if (fullPage) {
    return (
      <div className="flex items-center justify-center h-full">
        {content}
      </div>
    );
  }

  return content;
}
